import { DrivingLessonRecordType, toCZHoursString, toCzDateString } from '@evidia/shared';
import { Alert, Flex, Typography } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { useActiveSchoolId } from '../../hooks/useActiveSchoolId';
import { getDrivingLessonRecordTypeName } from '../../utils/naming';
import { trpc } from '../../utils/trcp';
import MainTitle from '../MainTitle';
import { useDrivingLessonsData } from '../StudentCard/hooks/useDrivingLessonsData';
import { resolveDrivingDataLessonsTitle } from '../StudentCard/utils';
import StudentGroup from '../StudentGroup';
import { StyledCard } from '../StyledCard';
import { Section } from './Section';
import SignatureForm from './SignatureForm';
import type { Props, PropsFromApp, PropsFromStudentPass } from './StudentPass.types';
import StudentPassShareButton from './StudentPassShareButton';

const StudentName = styled(Typography.Title)``;

const StudentInfoLabel = styled(Typography.Paragraph)`
	&.ant-typography {
		font-size: 14px;
		line-height: 22px;
	}
`;

const StudentInfoTitle = styled.h5`
	font-size: 12px;
	line-height: 20px;
	color: #8c8c8c;
	text-transform: uppercase;
	font-weight: 600;
`;

const Lesson = styled.div`
	display: grid;
	grid-template-columns: 70px auto 1fr;
	grid-template-rows: auto;
	column-gap: 16px;
	row-gap: 5px;
	padding: 20px;
	box-sizing: border-box;

	border-bottom: 1px solid #e4e8ed;

	width: calc(100% + 48px);
	transform: translateX(-24px);

	& > span {
		grid-column: span 2;
	}
`;

const Signature = styled.div`
	svg {
		height: 40px;
		width: auto;
	}

	grid-row: span 2;
	justify-self: center;
`;

const isPropsFromStudentPass = (props: Props): props is PropsFromStudentPass => 'data' in props;

const StudentPass: React.FC<Props> = props => {
	if (isPropsFromStudentPass(props)) {
		const { data, secretCode, schoolId, registrationNumber } = props;
		return (
			<StyledCard>
				<StudentPassInternal
					data={data}
					secretCode={secretCode}
					schoolId={schoolId}
					registrationNumber={registrationNumber}
					type="student"
				/>
			</StyledCard>
		);
	}

	return <StudentPassWithData {...props} />;
};

const StudentPassWithData: React.FC<PropsFromApp> = ({ studentId, type }) => {
	const { activeSchoolId } = useActiveSchoolId();

	const getStudentPass = trpc.student.getStudentPass.useQuery({
		type: 'app',
		schoolId: activeSchoolId,
		studentId,
	});

	if (getStudentPass.isLoading) {
		return <Alert message="Načítání..." type="info" />;
	}

	if (getStudentPass.isError) {
		return <Alert message="Nastala chyba" type="error" />;
	}

	if (typeof getStudentPass.data === 'string') {
		return <Alert message={getStudentPass.data} type="error" />;
	}

	return <StudentPassInternal data={getStudentPass.data} type={type} />;
};

const StudentPassInternal: React.FC<PropsFromStudentPass> = ({
	data,
	secretCode,
	schoolId,
	registrationNumber,
	type,
}) => {
	const { activeSchoolId } = useActiveSchoolId();
	const isStudentMode = activeSchoolId === 'activeSchoolId-not-set';
	const { student, school } = data;

	const payload = {
		secretCode,
		schoolId,
		registrationNumber,
	};
	const { drivingLessonsData } = useDrivingLessonsData(activeSchoolId, student.id, schoolId ? payload : undefined);

	const getStudentName = () =>
		[student.studentInfo?.degree, student.studentInfo?.firstName, student.studentInfo?.lastName]
			.filter(Boolean)
			.join(' ');

	return (
		<div data-testid="student-pass">
			<Flex gap={40} align="flex-start" style={{ maxWidth: '400px', marginBottom: 20 }}>
				<MainTitle align="left" level={2}>
					Elektronický průkaz žadatele o řidičské oprávnění
				</MainTitle>
				{!isStudentMode && <StudentPassShareButton activeSchoolId={activeSchoolId} studentId={student.id} />}
			</Flex>

			<Section
				title={school.schoolInfo.name}
				paragraphs={[
					`Číslo autoškoly: ${school.schoolInfo.number}`,
					`${school.schoolAddress.street} ${school.schoolAddress.streetCode}`,
					` ${school.schoolAddress.city}, ${school.schoolAddress.postCode}`,
				]}
			/>

			<Section
				title={school.schoolOperator?.name ?? 'Název provozovatele'}
				paragraphs={[
					// eslint-disable-next-line max-len
					`${school.schoolOperator?.street ?? 'Provozovnicka'} ${school.schoolOperator?.streetCode ?? '2241/2'}`,
					`${school.schoolOperator?.city ?? 'Praha'}, ${school.schoolOperator?.postCode ?? '25901'}`,
				]}
			/>

			<StudentName level={3}>{`${getStudentName()}`}</StudentName>
			<div>
				<StudentInfoTitle>Evidenční číslo</StudentInfoTitle>
				<StudentInfoLabel>{student.registrationNumber}</StudentInfoLabel>
			</div>
			<div>
				<StudentInfoTitle>Skupina</StudentInfoTitle>
				<StudentInfoLabel>
					{student.studentStudyInfo!.group.map((g, i) => (
						<React.Fragment key={g}>
							<StudentGroup type={g} />
							{i !== student.studentStudyInfo!.group.length - 1 && ', '}
						</React.Fragment>
					))}
				</StudentInfoLabel>
			</div>
			<div>
				<StudentInfoTitle>Podpis žáka</StudentInfoTitle>
				<SignatureForm
					studentId={student.id}
					secretCode={secretCode}
					schoolId={schoolId ?? activeSchoolId}
					registrationNumber={registrationNumber}
					type={type}
				/>
			</div>
			<Typography.Title level={4} style={{ marginTop: 40 }}>
				{getDrivingLessonRecordTypeName(DrivingLessonRecordType.DRIVING)}
			</Typography.Title>
			<Flex vertical>
				{drivingLessonsData.DRIVING?.map(lesson => {
					const { title, hasError } = resolveDrivingDataLessonsTitle(lesson);

					return (
						<Lesson key={lesson.id}>
							<span style={{ color: hasError ? 'red' : '#595959' }}>{title}</span>
							{lesson.signature && <Signature dangerouslySetInnerHTML={{ __html: lesson.signature }} />}
							<div>{toCzDateString(lesson.startTime)}</div>
							<div>{`${toCZHoursString(lesson.startTime)} - ${toCZHoursString(lesson.endTime)}`}</div>
						</Lesson>
					);
				})}
			</Flex>
			<Typography.Title level={4} style={{ marginTop: 40 }}>
				{getDrivingLessonRecordTypeName(DrivingLessonRecordType.PV_UV)}
			</Typography.Title>
			<Flex vertical>
				{drivingLessonsData.PV_UV?.map(lesson => {
					const { title, hasError } = resolveDrivingDataLessonsTitle(lesson, 'Lekce');

					return (
						<Lesson key={lesson.id}>
							<span style={{ color: hasError ? 'red' : '#595959' }}>{title}</span>
							<div>{toCzDateString(lesson.startTime)}</div>
							<div>{`${toCZHoursString(lesson.startTime)} - ${toCZHoursString(lesson.endTime)}`}</div>
						</Lesson>
					);
				})}
			</Flex>
			<Typography.Title level={4} style={{ marginTop: 40 }}>
				{getDrivingLessonRecordTypeName(DrivingLessonRecordType.PV_ZP)}
			</Typography.Title>
			<Flex vertical>
				{drivingLessonsData.PV_ZP?.map(lesson => {
					const { title, hasError } = resolveDrivingDataLessonsTitle(lesson, 'Lekce');

					return (
						<Lesson key={lesson.id}>
							<span style={{ color: hasError ? 'red' : '#595959' }}>{title}</span>
							<div>{toCzDateString(lesson.startTime)}</div>
							<div>{`${toCZHoursString(lesson.startTime)} - ${toCZHoursString(lesson.endTime)}`}</div>
						</Lesson>
					);
				})}
			</Flex>
		</div>
	);
};

export default StudentPass;
